import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HbrButton,
  HbrDomTable,
  HbrIcon,
  HbrPagination,
  HbrSpinner,
} from '@harbor/elements-react-standalone';
import { caretRight } from '@harbor/elements/icons';

import {
  Column,
  ColumnAlignment,
  ColumnPin,
  ColumnType,
  DataType,
  getColumnTypeConfig,
} from '../../../types/table';

import { fetchItems, postItem, putItem, deleteItem } from '../../../support/queries';
import { onScreenSizeChange, formatDateOrTBD } from '../../../support/utils';
import StbModal from '../../security-toolbox-ui/StbModal';
import PovOpportunityProductRowsTable from './PovOpportunityProductRowsTable';
import PovSemNotesPanel from './PovSemNotesPanel';
import { useSfTeamOpportunitiesPovLean } from '../../../hooks/seSfTeamOpportunitiesPov';
import { useSfPovActivityDetail } from '../../../hooks/useSfTeamPovActivitySummary';
import { povOpportunitiesWithActivities } from '../../../support/povActivityOpportunities';
import { useAuthenticateUser } from '../../../hooks/useAuthenticateUser';
import StbButton from '../../security-toolbox-ui/StbButton';

type OppRow = {
  id: string;
  expandable: boolean;
  oppId: string;
  accountId: string;
  Name: string;
  AccountName?: string | null;
  Amount?: number | null;
  StageName?: string | null;
  CloseDate?: string | null;
  OwnerName?: string | null;
  rm?: string | null;
  Cisco_AM__c?: string | null;
  Cisco_Terr_4__c?: string | null;
  Cisco_Terr_5__c?: string | null;
  has_pov: boolean;
  pov_product_count: number;
  pov_rows: any[];
  activity_count: number;
  activities: any[];
};

function safeRowId(s: string) {
  return String(s || '').replace(/[^a-z0-9_-]/gi, '_');
}

function formatCurrencyUSD(n: any) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(v);
}

interface Props {
  email: string;
  /** `current_time_filter` for the list (`all` or `range:...`); opportunities are kept by Close Date. */
  timeFilter?: string;
  /** Hold the request until the fiscal window is known. */
  timePending?: boolean;
}

export default function PovIndividualContributorOppsTable({ email, timeFilter = 'all', timePending = false }: Props) {
  const { email: loggedInEmail } = useAuthenticateUser();
  // The list is the opportunities that have POV SE activities in Salesforce; the lean POV data (products, owners)
  // only enriches them.
  const {
    data: detail,
    isLoading: isLoadingDetail,
    error: detailError,
  } = useSfPovActivityDetail(email, timeFilter, { enabled: !!email && !timePending });
  const { data, isLoading: isLoadingLean, refetch } = useSfTeamOpportunitiesPovLean(email, {
    enabled: !!email && !timePending,
    currentTimeFilter: timeFilter,
  });
  const isLoading = isLoadingDetail || isLoadingLean;
  const error = detailError;

  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);

  const [expanded, setExpanded] = useState<string[]>([]);
  const [productDetailOpen, setProductDetailOpen] = useState(false);
  const [selectedProductRow, setSelectedProductRow] = useState<any | null>(null);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('asc');
  const [sortColumnId, setSortColumnId] = useState<string>('CloseDate');

  const [semModalOpen, setSemModalOpen] = useState(false);
  const [semRow, setSemRow] = useState<any | null>(null);
  const [semNotes, setSemNotes] = useState<any[]>([]);
  const [semLoading, setSemLoading] = useState(false);

  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    const off = onScreenSizeChange(({ width }) => setScreenWidth(width));
    return off;
  }, []);

  const allOpps: any[] = useMemo(() => {
    if (!data || typeof data !== 'object') return [];
    const key = Object.keys(data)[0];
    const block = (data as any)[key];
    return Array.isArray(block?.opportunities) ? block.opportunities : [];
  }, [data]);

  const originalRows: OppRow[] = useMemo(
    () =>
      povOpportunitiesWithActivities(detail?.opportunities, allOpps).map((o) => ({
        id: `opp_${safeRowId(o.oppId)}`,
        expandable: true,
        oppId: o.oppId,
        accountId: o.accountId,
        Name: o.name,
        AccountName: o.accountName,
        Amount: o.amount,
        StageName: o.stage,
        CloseDate: o.closeDate,
        OwnerName: o.ownerName,
        rm: o.rm,
        Cisco_AM__c: o.portfolioAe,
        Cisco_Terr_4__c: o.sl4,
        Cisco_Terr_5__c: o.sl5,
        has_pov: o.povRows.length > 0,
        pov_product_count: o.povRows.length,
        pov_rows: o.povRows,
        activity_count: o.activities.length,
        activities: o.activities,
      })),
    [detail, allOpps],
  );

  const isCompact = screenWidth < 1500;
  const isUltraCompact = screenWidth < 1200;

  const columns: Column[] = useMemo(() => {
    const commonLead: any[] = [
      {
        id: 'expandable',
        name: '',
        width: 56,
        maxWidth: 56,
        columnType: ColumnType.Custom,
        sortable: false,
        reorderable: false,
        resizable: false,
        pin: ColumnPin.Left,
        align: ColumnAlignment.Center,
        dataType: DataType.String,
      },
      // Percentage widths (never fixed px) so the columns shrink WITH the 100%-wide table instead of
      // forcing horizontal scroll; the trailing `empty` filler (width:'100%') absorbs any remainder.
      // The pinned `expandable` column stays a fixed px width — the pin-offset calc reads its px width.
      { id: 'Name', name: 'Opportunity', width: isUltraCompact ? '15%' : '14%', columnType: ColumnType.Custom, sortable: true, type: 'string' },
      { id: 'AccountName', name: 'Account', width: isUltraCompact ? '13%' : '12%', columnType: ColumnType.Custom, sortable: true, type: 'string' },
      { id: 'activity_count', name: 'POV SE Activities', width: '9%', columnType: ColumnType.Number, align: ColumnAlignment.Right, sortable: true, type: 'number' },
      { id: 'pov_product_count', name: 'Products', width: '6%', columnType: ColumnType.Number, align: ColumnAlignment.Right, sortable: true, type: 'number' },
      { id: 'Amount', name: 'Amount', width: '8%', columnType: ColumnType.Number, align: ColumnAlignment.Right, sortable: true, type: 'number' },
      { id: 'StageName', name: 'Stage', width: '10%', columnType: ColumnType.String, sortable: true, type: 'string' },
      { id: 'CloseDate', name: 'Close Date', width: '8%', columnType: ColumnType.Date, sortable: true, type: 'date' },
      { id: 'OwnerName', name: 'GSSO AE', width: '9%', columnType: ColumnType.String, sortable: true, type: 'string' },
    ];

    const wideOnly: any[] = isCompact
      ? []
      : [
        { id: 'rm', name: 'RM', width: '5%', columnType: ColumnType.String, sortable: true, type: 'string' },
        { id: 'Cisco_AM__c', name: 'Portfolio AE', width: '6%', columnType: ColumnType.String, sortable: true, type: 'string' },
        { id: 'Cisco_Terr_4__c', name: 'SL4', width: '5%', columnType: ColumnType.String, sortable: true, type: 'string' },
        { id: 'Cisco_Terr_5__c', name: 'SL5', width: '5%', columnType: ColumnType.String, sortable: true, type: 'string' },
      ];

    const fill = [{ id: 'empty', name: '', width: '100%', columnType: ColumnType.Custom, sortable: false }];

    return [...commonLead, ...wideOnly, ...fill].map((c) =>
      getColumnTypeConfig({
        ...c,
        resizable: false,
        reorderable: false,
      })
    );
  }, [isCompact, isUltraCompact]);

  const sortedRows: OppRow[] = useMemo(() => {
    const arr = [...originalRows];
    if (!sortOrder || !sortColumnId) return arr;

    const dir = sortOrder === 'asc' ? 1 : -1;
    arr.sort((a: any, b: any) => {
      const av = a[sortColumnId];
      const bv = b[sortColumnId];

      if (sortColumnId === 'Amount' || sortColumnId === 'pov_product_count' || sortColumnId === 'activity_count') {
        return (Number(av ?? 0) - Number(bv ?? 0)) * dir;
      }

      if (sortColumnId === 'CloseDate') {
        const ad = av ? new Date(av).getTime() : Number.POSITIVE_INFINITY;
        const bd = bv ? new Date(bv).getTime() : Number.POSITIVE_INFINITY;
        return (ad - bd) * dir;
      }

      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });

    return arr;
  }, [originalRows, sortColumnId, sortOrder]);

  const totalCount = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePageIndex = Math.min(pageIndex, totalPages - 1);

  const pagedRows = useMemo(() => {
    const start = safePageIndex * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, safePageIndex, pageSize]);

  const toggleExpanded = useCallback((rowId: string) => {
    setExpanded((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
  }, []);

  const openProductDetails = (row: any) => {
    setSelectedProductRow(row);
    setProductDetailOpen(true);
  };

  const openSemNotesModal = async (row: any) => {
    setSemRow(row);
    setSemModalOpen(true);
    setSemLoading(true);
    setNewNote('');

    try {
      const opProductId = row?.opportunity_product_id;
      const notes = await fetchItems('db/sem-note', { opportunity_product: opProductId }, { includePageSize: false });
      setSemNotes(Array.isArray(notes) ? notes : []);
    } catch (e) {
      console.error('Failed loading SEM notes', e);
      setSemNotes([]);
    } finally {
      setSemLoading(false);
    }
  };

  const handleCreateSemNote = async () => {
    if (!semRow?.opportunity_product_id || !newNote.trim()) return;
    try {
      await postItem('db/sem-note', {
        opportunity_product: semRow.opportunity_product_id,
        note: newNote.trim(),
      });
      setNewNote('');
      await openSemNotesModal(semRow);
      refetch();
    } catch (e) {
      console.error('Failed creating SEM note', e);
    }
  };

  const renderHeader = () =>
    // No `w-max` here: that forced each header to its single-line max-content width, expanding the
    // column past its percentage share. Let the label wrap/ellipsize within the allotted width instead.
    columns.map((column: any) => (
      <div key={`column-${column.id}`} slot={`column-${column.id}`}>
        <div
          className="flex items-center relative"
          style={{ justifyContent: column.align === ColumnAlignment.Right ? 'flex-end' : 'flex-start' }}
        >
          <div>{column.name}</div>
        </div>
      </div>
    ));

  const renderBody = () => {
    if (!pagedRows.length) return null;
    const list: JSX.Element[] = [];

    pagedRows.forEach((row) => {
      columns.forEach((column: any) => {
        const cellId = `row-${row.id}-column-${column.id}`;

        if (column.id === 'expandable') {
          list.push(
            <div key={cellId} slot={cellId} className="min-h-8 flex items-center justify-center">
              <HbrButton
                variant="text"
                size="xsmall"
                aria={{ label: 'expandable row' }}
                onClick={(evt: any) => {
                  evt.stopPropagation();
                  toggleExpanded(row.id);
                }}
              >
                <HbrIcon asset={caretRight} className={expanded.includes(row.id) ? 'hbr-css__expandable-btn-expanded' : ''} />
              </HbrButton>
            </div>
          );
          return;
        }

        if (column.id === 'Name') {
          list.push(
            <div key={cellId} slot={cellId} className="min-w-0 font-semibold hover:text-link hover:underline cursor-pointer min-h-8 flex items-center truncate" title={row.Name}>
              <Link to={`/opportunity/${row.oppId}`} className="truncate">{row.Name}</Link>
            </div>
          );
          return;
        }

        if (column.id === 'AccountName') {
          list.push(
            <div key={cellId} slot={cellId} className="min-w-0 font-semibold hover:text-link hover:underline cursor-pointer min-h-8 flex items-center truncate" title={row.AccountName ?? ''}>
              <Link to={`/customer/${row.accountId}`} className="truncate">{row.AccountName ?? ''}</Link>
            </div>
          );
          return;
        }

        if (column.id === 'Amount') {
          list.push(
            <div key={cellId} slot={cellId} className="min-h-8 flex items-center box-content" style={{ justifyContent: 'flex-end' }}>
              {formatCurrencyUSD(row.Amount)}
            </div>
          );
          return;
        }

        if (column.id === 'CloseDate') {
          list.push(
            <div key={cellId} slot={cellId} className="min-h-8 flex items-center box-content">
              {formatDateOrTBD(row.CloseDate)}
            </div>
          );
          return;
        }

        if (column.id === 'empty') {
          list.push(<div key={cellId} slot={cellId} className="min-h-8 flex items-center" />);
          return;
        }

        const v = (row as any)[column.id];
        list.push(
          <div
            key={cellId}
            slot={cellId}
            className="min-h-8 flex items-center box-content"
            style={column.align === ColumnAlignment.Right ? { justifyContent: 'flex-end' } : undefined}
          >
            {v ?? ''}
          </div>
        );
      });

      if (row.expandable && expanded.includes(row.id)) {
        const expandableId = `row-${row.id}-expandable`;
        list.push(
          <div key={expandableId} slot={expandableId} className="hbr-css__table-child-container" style={{ padding: 12, width: '100%', minWidth: 0 }}>
            <div className="hbr-css__text-primary-sm-semibold mb-1">POV SE Activities</div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label={`POV SE activities for ${row.Name}`}>
              {row.activities.map((a: any) => {
                const facts: Array<[string, string]> = [
                  ['Service type', a.service_type],
                  ['Duration', a.duration_days != null ? `${a.duration_days} ${a.duration_days === 1 ? 'day' : 'days'}` : ''],
                  ['Priority', a.priority],
                  ['Technology', a.technology],
                  ['Next step', a.next_step],
                ].filter(([, v]) => !!v) as Array<[string, string]>;
                return (
                  <li key={a.id} style={{ padding: '6px 0', borderTop: '1px solid var(--hbr-color-border, rgba(128,128,128,.25))' }}>
                    <div className="flex flex-wrap items-baseline gap-x-3">
                      <span className="hbr-css__text-secondary-sm" style={{ minWidth: 80 }}>{a.date ? formatDateOrTBD(a.date) : ''}</span>
                      <span className="font-semibold">{a.subject || '(no subject)'}</span>
                      {a.status ? <span className="hbr-css__text-secondary-sm">{a.status}</span> : null}
                      {a.url ? (
                        <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-link hover:underline hbr-css__text-secondary-sm">
                          Open in Salesforce
                        </a>
                      ) : null}
                    </div>
                    {facts.length > 0 && (
                      <div className="hbr-css__text-secondary-sm flex flex-wrap gap-x-4" style={{ marginTop: 2 }}>
                        {facts.map(([label, value]) => (
                          <span key={label}>
                            <b>{label}:</b> {value}
                          </span>
                        ))}
                      </div>
                    )}
                    {a.description ? (
                      <div style={{ marginTop: 4, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{a.description}</div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {row.has_pov && row.pov_rows?.length > 0 && (
              <div style={{ width: '100%', minWidth: 0, overflow: 'hidden', marginTop: 12 }}>
                <div className="hbr-css__text-primary-sm-semibold mb-1">POV Products</div>
                <PovOpportunityProductRowsTable
                  rows={row.pov_rows}
                  onOpenSemNotes={openSemNotesModal}
                  onOpenDetails={openProductDetails}
                />
              </div>
            )}
          </div>
        );
      }
    });

    return list;
  };

  const handleSort = (event: any) => {
    const nextOrder = event?.detail?.order as 'asc' | 'desc' | undefined;
    const col = event?.detail?.column;
    if (!col?.id) return;

    if (!nextOrder) {
      setSortOrder(null);
      setSortColumnId(col.id);
      setExpanded([]);
      setPageIndex(0);
      return;
    }

    setSortOrder(nextOrder);
    setSortColumnId(col.id);
    setExpanded([]);
    setPageIndex(0);
  };

  const handleSizeChanged = (e: any) => {
    setPageSize(Number(e.detail.pageSize));
    setExpanded([]);
    setPageIndex(0);
  };

  const handlePageChanged = (e: any) => {
    setExpanded([]);
    setPageIndex(Number(e.detail));
  };

  useEffect(() => {
    if (pageIndex !== safePageIndex) setPageIndex(safePageIndex);
  }, [safePageIndex, pageIndex]);

  if (timePending || isLoading) return <div style={{ padding: 12 }}><HbrSpinner>Loading POV opportunities…</HbrSpinner></div>;
  if (error) return <div style={{ padding: 12, color: 'red' }}>POV SE activity data is unavailable right now.</div>;
  if (!sortedRows.length) return <div style={{ padding: 12 }}>No opportunities with POV SE activities found.</div>;

  return (
    <div style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <div style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
        <HbrDomTable
          expanded={expanded}
          columns={columns as any}
          data={pagedRows}
          onHbr-sort={handleSort}
          autoLayout={true}
          style={{ display: 'block', width: '100%' }}
        >
          {renderHeader()}
          {renderBody()}
        </HbrDomTable>
      </div>

      {totalCount > pageSize && (
        <div className="py-2">
          <HbrPagination
            itemCount={totalCount}
            pageSize={pageSize}
            page={safePageIndex}
            sizeChanger={true}
            pageInfo={true}
            pageOptions={[10, 20, 30, 50, 100]}
            onHbr-size-changed={handleSizeChanged}
            onHbr-page-changed={handlePageChanged}
          />
        </div>
      )}

      <StbModal
        role="information"
        title={`SEM Notes${semRow?.product?.name ? ` - ${semRow.product.name}` : ''}`}
        open={semModalOpen}
        onSubmit={() => Promise.resolve()}
        onClose={async () => setSemModalOpen(false)}
      >
        <div className="flex flex-col gap-3">
          {semLoading ? (
            <HbrSpinner>Loading SEM notes…</HbrSpinner>
          ) : (
            <>
              <PovSemNotesPanel
                notes={semNotes}
                currentUserEmail={loggedInEmail || ''}
                onEdit={async (id, note) => {
                  await putItem('db/sem-note', {
                    id,
                    opportunity_product: semRow?.opportunity_product_id,
                    note,
                  } as any);
                  await openSemNotesModal(semRow);
                  refetch();
                }}
                onDelete={async (id) => {
                  await deleteItem(`db/sem-note/${id}/`);
                  await openSemNotesModal(semRow);
                  refetch();
                }}
              />

              <div className="border-t pt-3">
                <div className="text-sm font-semibold mb-2">Add Note</div>
                <textarea
                  className="w-full border rounded p-2 text-sm"
                  rows={4}
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />
                <div className="mt-2">
                  <StbButton
                    role="add"
                    size="small"
                    variant="outline"
                    onClick={() => handleCreateSemNote()}
                    disabled={!newNote.trim()}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </StbModal>

      <StbModal
        role="information"
        title={selectedProductRow?.product?.name ? `Product Details - ${selectedProductRow.product.name}` : 'Product Details'}
        open={productDetailOpen}
        onSubmit={() => Promise.resolve()}
        onClose={async () => setProductDetailOpen(false)}
      >
        {selectedProductRow ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><b>Quote Delivered:</b> {selectedProductRow.quote_delivered ?? ''}</div>
            <div><b>SE Aligned:</b> {selectedProductRow.se_aligned ?? ''}</div>
            <div><b>EDP Partner:</b> {selectedProductRow.edp_partner ?? ''}</div>
            <div><b>Competitor:</b> {selectedProductRow.competitor ?? ''}</div>
            <div><b>CSP:</b> {selectedProductRow.csp ?? ''}</div>
            <div><b>POV Pursuit Plan:</b> {selectedProductRow.pov_pursuit_plan ?? ''}</div>
            <div><b>Description/Technology:</b> {selectedProductRow.description_technology ?? ''}</div>
            <div>
              <b>S&C Opportunity:</b>{' '}
              {selectedProductRow.sc_link_url ? (
                <a
                  href={`${selectedProductRow.sc_link_url.replace(/\/$/, '')}/view`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-link hover:underline"
                >
                  {selectedProductRow.sc_link_name || selectedProductRow.sc_opportunity_id}
                </a>
              ) : (
                selectedProductRow.sc_link_name || selectedProductRow.sc_opportunity_id || ''
              )}
            </div>
            <div><b>Kickoff:</b> {formatDateOrTBD(selectedProductRow.pov_kickoff_date)}</div>
            <div><b>Expected Complete:</b> {formatDateOrTBD(selectedProductRow.expected_complete_date)}</div>
            <div><b>Expected Close:</b> {formatDateOrTBD(selectedProductRow.expected_close_date)}</div>
            <div><b>Kickoff Month/FY/Qtr:</b> {`${selectedProductRow.kickoff_month ?? ''} ${selectedProductRow.kickoff_fy ?? ''} ${selectedProductRow.kickoff_qtr ?? ''}`}</div>
            <div><b>Accelerate Close:</b> {selectedProductRow.accelerate_close ?? ''}</div>
            <div><b>Fiscal QTR:</b> {selectedProductRow.fiscal_qtr ?? ''}</div>
            <div><b>Arch Vision:</b> {selectedProductRow.arch_vision ?? ''}</div>
            <div><b>RDR:</b> {selectedProductRow.rdr ?? ''}</div>
            <div><b>Pipeline $:</b> {selectedProductRow.pipeline_amount ?? ''}</div>
            <div><b>Bkgs $:</b> {selectedProductRow.bkgs_amount ?? ''}</div>
          </div>
        ) : null}
      </StbModal>
    </div>
  );
}